/**
 * Agenda Shelly — contrato jobs[] (doc 94).
 */
import axios from 'axios';
import { getApiBaseUrl } from '../config';

export interface TimerConfig {
  enabled: boolean;
  autoOnEnabled: boolean;
  autoOffEnabled: boolean;
  oneTimeOnly: boolean;
  autoOnHours: number;
  autoOnMinutes: number;
  autoOnSeconds: number;
  autoOffHours: number;
  autoOffMinutes: number;
  autoOffSeconds: number;
}

/** @deprecated formato wizard viejo — solo para migración en UI */
export interface ScheduleTime {
  hours: number;
  minutes: number;
  seconds: number;
}

export type AgendaActionType =
  | 'On'
  | 'Off'
  | 'Toggle'
  | 'Pulse'
  | 'Open'
  | 'Close'
  | 'Stop'
  | 'GoToPosition'
  | string;

export interface AgendaJob {
  id: number;
  enabled: boolean;
  hours: number;
  minutes: number;
  seconds: number;
  selectedDays: number[];
  specificDaysEnabled?: boolean;
  selectedDaysOfMonth?: number[];
  specificMonthsEnabled?: boolean;
  selectedMonths?: number[];
  actionType: AgendaActionType;
  toggleEnabled?: boolean;
  toggleAfterSeconds?: number;
  brightness?: number | null;
  coverPosition?: number | null;
  editable?: boolean;
  rawTimespec?: string | null;
  note?: string | null;
}

export interface AgendaConfig {
  enabled: boolean;
  jobs: AgendaJob[];
  // legacy keys opcionales (lectura / DeviceModal viejo)
  selectedDays?: number[];
  scheduleTimes?: ScheduleTime[];
  actionType?: string;
  toggleEnabled?: boolean;
  toggleAfterSeconds?: number;
  specificDaysEnabled?: boolean;
  specificMonthsEnabled?: boolean;
  selectedDaysOfMonth?: number[];
  selectedMonths?: number[];
  scheduleSeconds?: number[];
}

export interface DeviceSchedules {
  device_id: number;
  timer_config: TimerConfig;
  agenda_config: AgendaConfig;
  shelly_has_schedule?: boolean;
}

const deviceSchedulesCache = new Map<number, DeviceSchedules>();

export const defaultTimerConfig: TimerConfig = {
  enabled: false,
  autoOnEnabled: false,
  autoOffEnabled: false,
  oneTimeOnly: false,
  autoOnHours: 0,
  autoOnMinutes: 0,
  autoOnSeconds: 0,
  autoOffHours: 0,
  autoOffMinutes: 0,
  autoOffSeconds: 0,
};

export const defaultAgendaConfig: AgendaConfig = {
  enabled: false,
  jobs: [],
};

export function emptyAgenda(): AgendaConfig {
  return { enabled: false, jobs: [] };
}

export function isAgendaEmpty(cfg?: Partial<AgendaConfig> | AgendaConfig | null): boolean {
  if (!cfg) return true;
  if (Array.isArray(cfg.jobs)) return cfg.jobs.length === 0;
  const days = cfg.selectedDays || [];
  const times = cfg.scheduleTimes || [];
  const action = (cfg.actionType || '').trim();
  if (days.length || action) return false;
  if (times.length === 1) {
    const t = times[0];
    if (!t.hours && !t.minutes && !t.seconds) return true;
  }
  return times.length === 0;
}

export function normalizeAgendaConfig(raw?: Partial<AgendaConfig> | AgendaConfig | null): AgendaConfig {
  if (!raw || isAgendaEmpty(raw)) return emptyAgenda();
  if (Array.isArray(raw.jobs)) {
    return {
      enabled: Boolean(raw.enabled),
      jobs: raw.jobs.slice(0, 20).map((j, i) => ({
        id: j.id || i + 1,
        enabled: j.enabled !== false,
        hours: Math.min(23, Math.max(0, Number(j.hours) || 0)),
        minutes: Math.min(59, Math.max(0, Number(j.minutes) || 0)),
        seconds: Math.min(59, Math.max(0, Number(j.seconds) || 0)),
        selectedDays: (j.selectedDays?.length ? j.selectedDays : [1, 2, 3, 4, 5, 6, 7]).map(Number),
        specificDaysEnabled: Boolean(j.specificDaysEnabled),
        selectedDaysOfMonth: j.selectedDaysOfMonth || [],
        specificMonthsEnabled: Boolean(j.specificMonthsEnabled),
        selectedMonths: j.selectedMonths || [],
        actionType: j.actionType || 'On',
        toggleEnabled: Boolean(j.toggleEnabled),
        toggleAfterSeconds: Number(j.toggleAfterSeconds) || 0,
        brightness: j.brightness ?? null,
        coverPosition: j.coverPosition ?? null,
        editable: j.editable !== false,
        rawTimespec: j.rawTimespec,
        note: j.note,
      })),
    };
  }
  // legacy → un job por horario
  const times = raw.scheduleTimes || [];
  const days = raw.selectedDays?.length ? raw.selectedDays : [1, 2, 3, 4, 5, 6, 7];
  let action = (raw.actionType || 'On').trim();
  const map: Record<string, string> = {
    on: 'On', off: 'Off', toggle: 'Toggle', encender: 'On', apagar: 'Off',
    cambiar: 'Toggle', 'cambiar estado': 'Toggle', pulsar: 'Pulse',
  };
  action = map[action.toLowerCase()] || action || 'On';
  const jobs: AgendaJob[] = times
    .filter((t) => t && (t.hours || t.minutes || t.seconds || times.length > 1 || days.length))
    .map((t, i) => ({
      id: i + 1,
      enabled: true,
      hours: t.hours || 0,
      minutes: t.minutes || 0,
      seconds: t.seconds || 0,
      selectedDays: days,
      actionType: action,
      toggleEnabled: Boolean(raw.toggleEnabled),
      toggleAfterSeconds: Number(raw.toggleAfterSeconds) || 0,
      editable: true,
    }));
  return { enabled: Boolean(raw.enabled), jobs };
}

export function formatJobTime(j: AgendaJob, withSeconds = false): string {
  const base = `${String(j.hours).padStart(2, '0')}:${String(j.minutes).padStart(2, '0')}`;
  if (withSeconds) return `${base}:${String(j.seconds || 0).padStart(2, '0')}`;
  return base;
}

export function agendaActionsForType(tipo?: string, generacion?: number): AgendaActionType[] {
  const t = (tipo || '').toUpperCase();
  if (t === 'PLUS2PM') return ['Open', 'Close', 'Stop', 'GoToPosition'];
  if (t === 'PLUS1') return ['Pulse'];
  if (t === 'DIMMER2' || generacion === 1) return ['On', 'Off'];
  return ['On', 'Off', 'Toggle'];
}

export const getDeviceSchedules = async (deviceId: number, forceRefresh: boolean = false): Promise<DeviceSchedules> => {
  if (!forceRefresh) {
    const cached = deviceSchedulesCache.get(deviceId);
    if (cached) return cached;
  } else {
    deviceSchedulesCache.delete(deviceId);
  }
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No hay token de autenticación');
  try {
    const response = await axios.get(`${getApiBaseUrl()}/devices/${deviceId}/schedules`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = response.data;
    const result: DeviceSchedules = {
      device_id: deviceId,
      timer_config: data.timer_config || { ...defaultTimerConfig },
      agenda_config: normalizeAgendaConfig(data.agenda_config),
      shelly_has_schedule: Boolean(data.shelly_has_schedule),
    };
    deviceSchedulesCache.set(deviceId, result);
    return result;
  } catch {
    return {
      device_id: deviceId,
      timer_config: { ...defaultTimerConfig },
      agenda_config: emptyAgenda(),
      shelly_has_schedule: false,
    };
  }
};

export const updateDeviceSchedules = async (
  deviceId: number,
  timerConfig?: TimerConfig,
  agendaConfig?: AgendaConfig,
  opts?: { confirmClearSchedule?: boolean },
): Promise<{ success: boolean; message: string; agenda_config?: AgendaConfig; shelly_has_schedule?: boolean }> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No hay token de autenticación');
    const data: any = {};
    if (timerConfig !== undefined) data.timer_config = timerConfig;
    if (agendaConfig !== undefined) data.agenda_config = normalizeAgendaConfig(agendaConfig);
    if (opts?.confirmClearSchedule) data.confirm_clear_schedule = true;
    const response = await axios.put(`${getApiBaseUrl()}/devices/${deviceId}/schedules`, data, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    deviceSchedulesCache.delete(deviceId);
    return {
      success: true,
      message: response.data.message || 'Configuraciones actualizadas correctamente',
      agenda_config: response.data.agenda_config
        ? normalizeAgendaConfig(response.data.agenda_config)
        : agendaConfig,
      shelly_has_schedule: response.data.shelly_has_schedule,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.error || error.message || 'Error desconocido',
    };
  }
};

/** Pisar timer/agenda en caché cuando otro cliente guardó (evento socket). */
export function patchDeviceSchedulesCache(
  deviceId: number,
  patch: { timer_config?: TimerConfig | null; agenda_config?: AgendaConfig | null },
): void {
  const prev = deviceSchedulesCache.get(deviceId);
  if (!prev) return;
  deviceSchedulesCache.set(deviceId, {
    ...prev,
    timer_config: patch.timer_config != null ? patch.timer_config : prev.timer_config,
    agenda_config: patch.agenda_config != null
      ? normalizeAgendaConfig(patch.agenda_config)
      : prev.agenda_config,
  });
}

export const clearDeviceSchedulesCache = (deviceId: number) => {
  deviceSchedulesCache.delete(deviceId);
};
export const clearAllSchedulesCache = () => deviceSchedulesCache.clear();
export const getDeviceSchedulesFromCache = (deviceId: number) => deviceSchedulesCache.get(deviceId) || null;
export const hasDeviceSchedulesInCache = (deviceId: number) => deviceSchedulesCache.has(deviceId);
